import React from "react";
import theme from "../../utils/themeColors";

/**
 * Icon sizes
 */
export enum IconSize {
  EXTRA_SMALL = "xs",
  SMALL = "sm",
  MEDIUM = "md",
  LARGE = "lg",
  EXTRA_LARGE = "xl",
}

/**
 * Icon props interface
 */
export interface IconProps {
  size?: IconSize | number;
  color?: string;
  className?: string;
}

/**
 * Base Icon component
 */
const BaseIcon: React.FC<IconProps & { children: React.ReactNode }> = ({
  size = IconSize.MEDIUM,
  color,
  className = "",
  children,
}) => {
  // Get size in pixels
  const getSize = () => {
    switch (size) {
      case IconSize.EXTRA_SMALL:
        return 12;
      case IconSize.SMALL:
        return 16;
      case IconSize.MEDIUM:
        return 20;
      case IconSize.LARGE:
        return 24;
      case IconSize.EXTRA_LARGE:
        return 32;
      default:
        return typeof size === "number" ? size : 20;
    }
  };

  const pixelSize = getSize();
  const colorClass = color || "";

  return (
    <svg
      width={pixelSize}
      height={pixelSize}
      viewBox="0 0 24 24"
      className={`${colorClass} ${className}`}
      fill="none"
      stroke="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  );
};

/**
 * Refresh Icon
 */
export const RefreshIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </BaseIcon>
);

/**
 * Book Icon
 */
export const BookIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </BaseIcon>
);

/**
 * Heart Icon
 */
export const HeartIcon: React.FC<IconProps & { filled?: boolean }> = ({
  filled = false,
  ...props
}) => {
  if (filled) {
    return (
      <svg
        width={
          props.size === IconSize.SMALL
            ? 16
            : props.size === IconSize.LARGE
            ? 24
            : 20
        }
        height={
          props.size === IconSize.SMALL
            ? 16
            : props.size === IconSize.LARGE
            ? 24
            : 20
        }
        viewBox="0 0 16 16"
        className={`${props.className || ""} ${props.color || theme.bg.fill}`}
      >
        <path d="M14.682 2.318A4.485 4.485 0 0 0 11.5 1 4.377 4.377 0 0 0 8 2.707 4.383 4.383 0 0 0 4.5 1a4.5 4.5 0 0 0-3.182 7.682L8 15l6.682-6.318a4.5 4.5 0 0 0 0-6.364Z" />
      </svg>
    );
  }

  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} fill-current`}
    >
      <path d="M14.682 2.318A4.485 4.485 0 0 0 11.5 1 4.377 4.377 0 0 0 8 2.707 4.383 4.383 0 0 0 4.5 1a4.5 4.5 0 0 0-3.182 7.682L8 15l6.682-6.318a4.5 4.5 0 0 0 0-6.364Zm-1.4 4.933L8 12.247l-5.285-5A2.5 2.5 0 0 1 4.5 3c1.437 0 2.312.681 3.5 2.625C9.187 3.681 10.062 3 11.5 3a2.5 2.5 0 0 1 1.785 4.251h-.003Z" />
    </svg>
  );
};

/**
 * Share Icon
 */
export const ShareIcon: React.FC<IconProps & { filled?: boolean }> = ({
  filled = false,
  ...props
}) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} ${
        filled ? theme.bg.fill : "fill-current dark:fill-current"
      }`}
    >
      <path d="M13 7h2v6a1 1 0 0 1-1 1H4v2l-4-3 4-3v2h9V7ZM3 9H1V3a1 1 0 0 1 1-1h10V0l4 3-4 3V4H3v5Z" />
    </svg>
  );
};

/**
 * Comment Icon
 */
export const CommentIcon: React.FC<IconProps & { filled?: boolean }> = ({
  filled = false,
  ...props
}) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} ${
        filled ? theme.bg.fill : "fill-current dark:fill-current"
      }`}
    >
      <path d="M8 0C3.6 0 0 3.1 0 7s3.6 7 8 7h.6l5.4 2v-4.4c1.2-1.2 2-2.8 2-4.6 0-3.9-3.6-7-8-7zm4 10.8v2.3L8.9 12H8c-3.3 0-6-2.2-6-5s2.7-5 6-5 6 2.2 6 5c0 2.2-2 3.8-2 3.8z" />
    </svg>
  );
};

/**
 * Media Icon
 */
export const MediaIcon: React.FC<IconProps> = (props) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} fill-violet-400`}
    >
      <path d="M0 0h2v16H0V0Zm14 0h2v16h-2V0Zm-3 7H5c-.6 0-1-.4-1-1V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1v5c0 .6-.4 1-1 1ZM6 5h4V2H6v3Zm5 11H5c-.6 0-1-.4-1-1v-5c0-.6.4-1 1-1h6c.6 0 1 .4 1 1v5c0 .6-.4 1-1 1Zm-5-2h4v-3H6v3Z" />
    </svg>
  );
};

/**
 * Graph Icon
 */
export const GraphIcon: React.FC<IconProps> = (props) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} fill-violet-400`}
    >
      <path d="M6.974 14c-.3 0-.7-.2-.9-.5l-2.2-3.7-2.1 2.8c-.3.4-1 .5-1.4.2-.4-.3-.5-1-.2-1.4l3-4c.2-.3.5-.4.9-.4.3 0 .6.2.8.5l2 3.3 3.3-8.1c0-.4.4-.7.8-.7s.8.2.9.6l4 8c.2.5 0 1.1-.4 1.3-.5.2-1.1 0-1.3-.4l-3-6-3.2 7.9c-.2.4-.6.6-1 .6Z" />
    </svg>
  );
};

/**
 * Emoji Icon
 */
export const EmojiIcon: React.FC<IconProps> = (props) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} fill-violet-400`}
    >
      <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
    </svg>
  );
};

/**
 * Dashboard Icon
 */
export const DashboardIcon: React.FC<IconProps & { active?: boolean }> = ({
  active = false,
  ...props
}) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} shrink-0 fill-current ${
        active ? theme.text.brand : theme.text.inactive
      }`}
    >
      <path d="M5.936.278A7.983 7.983 0 0 1 8 0a8 8 0 1 1-8 8c0-.722.104-1.413.278-2.064a1 1 0 1 1 1.932.516A5.99 5.99 0 0 0 2 8a6 6 0 1 0 6-6c-.53 0-1.045.076-1.548.21A1 1 0 1 1 5.936.278Z" />
      <path d="M6.068 7.482A2.003 2.003 0 0 0 8 10a2 2 0 1 0-.518-3.932L3.707 2.293a1 1 0 0 0-1.414 1.414l3.775 3.775Z" />
    </svg>
  );
};

/**
 * Target Icon
 */
export const TargetIcon: React.FC<IconProps & { active?: boolean }> = ({
  active = false,
  ...props
}) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} shrink-0 fill-current ${
        active ? theme.text.brand : theme.text.inactive
      }`}
    >
      <path d="M14.75 2.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM14.75 16a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM2.5 14.75a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM1.25 2.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z" />
      <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2ZM4 8a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
    </svg>
  );
};

/**
 * Add Icon
 */
export const AddIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </BaseIcon>
);

/**
 * Arrow Left Icon
 */
export const ArrowLeftIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 17l-5-5m0 0l5-5m-5 5h12"
    />
  </BaseIcon>
);

/**
 * Copy Icon
 */
export const CopyIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z"
    />
  </BaseIcon>
);

/**
 * Components Icon
 */
export const ComponentsIcon: React.FC<IconProps> = (props) => (
  <svg
    className={`${props.className || ""} shrink-0 fill-current`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
  >
    <path d="M.06 10.003a1 1 0 0 1 1.948.455c-.019.08.01.152.078.19l5.83 3.333c.053.03.116.03.168 0l5.83-3.333a.163.163 0 0 0 .078-.188 1 1 0 0 1 1.947-.459 2.161 2.161 0 0 1-1.032 2.384l-5.83 3.331a2.168 2.168 0 0 1-2.154 0l-5.83-3.331a2.162 2.162 0 0 1-1.032-2.382Zm7.856-7.981-5.83 3.332a.17.17 0 0 0 0 .295l5.828 3.33c.054.031.118.031.17.002l5.83-3.333a.17.17 0 0 0 0-.294L8.085 2.023a.172.172 0 0 0-.17-.001ZM9.076.285l5.83 3.332c1.458.833 1.458 2.935 0 3.768l-5.83 3.333c-.667.38-1.485.38-2.153-.001l-5.83-3.332c-1.457-.833-1.457-2.935 0-3.767L6.925.285a2.173 2.173 0 0 1 2.15 0Z" />
  </svg>
);

/**
 * Plus Icon
 */
export const PlusIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </BaseIcon>
);

/**
 * Tutor Icon
 */
export const TutorIcon: React.FC<IconProps & { active?: boolean }> = ({
  active = false,
  ...props
}) => {
  return (
    <svg
      width={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      height={
        props.size === IconSize.SMALL
          ? 16
          : props.size === IconSize.LARGE
          ? 24
          : 20
      }
      viewBox="0 0 16 16"
      className={`${props.className || ""} shrink-0 fill-current ${
        active ? theme.text.brand : theme.text.inactive
      }`}
    >
      <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm1 13.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5zM10.5 5c0 .9-.7 1.5-1.5 1.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V6a.5.5 0 0 1 .5-.5c.3 0 .5-.2.5-.5s-.2-.5-.5-.5-.5.2-.5.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5C5 3.8 6.3 2.5 8 2.5s3 1.3 3 2.5z" />
    </svg>
  );
};

/**
 * Close Icon
 */
export const CloseIcon: React.FC<IconProps> = (props) => {
  return (
    <svg className="w-4 h-4 fill-current">
      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"></path>
    </svg>
  );
};

export default {
  RefreshIcon,
  BookIcon,
  HeartIcon,
  ShareIcon,
  CommentIcon,
  MediaIcon,
  GraphIcon,
  EmojiIcon,
  DashboardIcon,
  TargetIcon,
  AddIcon,
  TutorIcon,
  ArrowLeftIcon,
  CopyIcon,
  PlusIcon,
  CloseIcon,
};
